/**
 * Shared test ID registry for `data-testid` attributes.
 *
 * This file is the single source of truth for all test IDs used by both
 * frontend components and E2E tests. It has zero imports so it can be
 * consumed from any context (browser, Node, test runners) without side
 * effects.
 *
 * When adding a new test ID:
 *   1. Add it here under the appropriate section
 *   2. Use it in the component via `makeTestId(TEST_ID.page.move, 'submit-button')`
 *      or reference `TEST_ID.move.submitButton` directly
 *   3. E2E tests import this constant and get compile-time safety
 */
/**
 * Slugifies a string for use in test IDs. Shared by makeTestId and e2e
 * selector generation so both sides produce identical strings.
 */
export function naiveSlugify(str: string): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

const SEND_TEST_IDS = {
  sourceAssetSelectTrigger: 'send-source-asset-select-trigger',
  percentageButton25: 'send-percentage-button-25',
  percentageButton50: 'send-percentage-button-50',
  percentageButtonMax: 'send-percentage-button-max',
  reviewButton: 'send-review-button',
  submitButton: 'send-submit-button',
} as const;

export const TEST_ID = {
  layout: {
    sidebar: 'layout-sidebar',
  },

  /** Page-level prefixes used with `makeTestId(TEST_ID.page.x, ...)` */
  page: {
    home: 'page-home',
    wallet: 'page-wallet',
    move: 'page-move',
    swidge: 'page-swidge',
    activity: 'page-activity',
    portfolio: 'page-portfolio',
    perps: 'page-perps',
    gas: 'page-gas-account',
  },

  /** Fully-qualified test IDs for navigation elements */
  navigation: {
    home: 'navigation-home',
    portfolio: 'navigation-portfolio',
    activity: 'navigation-activity',
    swidge: 'navigation-swidge',
    search: 'navigation-search',
    perps: 'navigation-perps',
    crates: 'navigation-crates',
    more: 'navigation-more',
    send: 'navigation-send',
    move: 'navigation-move',
    deposit: 'navigation-deposit',
    buy: 'navigation-buy',
  },

  /**
   * Fully-qualified test IDs for the Send feature.
   * Also available as `TEST_ID.feature.send` for backward compatibility.
   */
  send: SEND_TEST_IDS,

  /** @deprecated Use `TEST_ID.send` directly. Kept for backward compatibility. */
  feature: {
    send: SEND_TEST_IDS,
  },

  /** Fully-qualified test IDs for the Move feature */
  move: {
    directionToggle: 'page-move-direction-toggle',
    fromCustodyTrigger: 'page-move-from-custody-trigger',
    toCustodyTrigger: 'page-move-to-custody-trigger',
    assetSelectTrigger: 'page-move-asset-select-trigger',
    assetSelectDialogSearch: 'page-move-asset-select-dialog-search',
    amountInput: 'page-move-amount-input',
    switchUnitButton: 'page-move-switch-unit-button',
    increaseToFullBalanceButton: 'page-move-increase-to-full-balance-button',
    submitButton: 'page-move-submit-button',
    closeButton: 'page-move-close-button',
    movingAssets: 'page-move-moving-assets',
    moveCompleted: 'page-move-move-completed',
  },

  /** Fully-qualified test IDs for the wallet page */
  wallet: {
    percentageButton25: 'page-wallet-percentage-button-25',
    percentageButton50: 'page-wallet-percentage-button-50',
    percentageButtonMax: 'page-wallet-percentage-button-max',
  },

  /** Fully-qualified test IDs for the Swidge feature */
  swidge: {
    sourceAssetSelectTrigger: 'page-swidge-source-asset-select-trigger',
    receiveAssetSelectTrigger: 'page-swidge-receive-asset-select-trigger',
    directionToggle: 'page-swidge-direction-toggle',
    submitButton: 'page-swidge-submit-button',
    slippageDisplay: 'page-swidge-slippage-display',
    prioritySwitch: 'page-swidge-priority-switch',
    percentageButton25: 'page-swidge-percentage-button-25',
    percentageButtonMax: 'page-swidge-percentage-button-max',
    refreshQuoteButton: 'page-swidge-refresh-quote-button',
    /**
     * Dynamic: `page-swidge-quote-card-{providerName}`.
     * Use with makeTestId(TEST_ID.page.swidge, 'quote-card', providerName).
     */
    quoteCardPrefix: 'page-swidge-quote-card',
    /**
     * Dynamic: `page-swidge-quote-provider-{providerName}`.
     * Use with makeTestId(TEST_ID.page.swidge, 'quote-provider', providerName).
     */
    quoteProviderPrefix: 'page-swidge-quote-provider',
  },

  /** Fully-qualified test IDs for the slippage settings */
  slippageSettings: {
    radioCustom: 'slippage-settings-radio-custom',
    radioAuto: 'slippage-settings-radio-auto',
  },

  /** Fully-qualified test IDs for Perps */
  perps: {
    balanceDisplay: 'page-perps-balance-display',
    marginAccountLabel: 'page-perps-margin-account-label',
    depositTriggerDesktop: 'page-perps-deposit-trigger-desktop',
    depositTriggerMobile: 'page-perps-deposit-trigger-mobile',
    withdrawTriggerDesktop: 'page-perps-withdraw-trigger-desktop',
    withdrawTriggerMobile: 'page-perps-withdraw-trigger-mobile',
    hyperliquid: {
      sourceAssetSelectTrigger:
        'page-perps-hyperliquid-source-asset-select-trigger',
      amountInput: 'page-perps-hyperliquid-amount-input',
      submitButton: 'page-perps-hyperliquid-submit-button',
      depositProcessing: 'page-perps-hyperliquid-deposit-processing',
      depositSuccess: 'page-perps-hyperliquid-deposit-success',
      depositDoneButton: 'page-perps-hyperliquid-deposit-done-button',
      withdrawAmountInput: 'page-perps-hyperliquid-withdraw-amount-input',
      withdrawalSubmit: 'page-perps-hyperliquid-withdrawal-submit',
      withdrawProcessing: 'page-perps-hyperliquid-withdraw-processing',
      withdrawDoneButton: 'page-perps-hyperliquid-withdraw-done-button',
    },
  },

  /** Fully-qualified test IDs for the Activity page */
  activity: {
    activityTable: 'page-activity-activity-table',
  },

  /** Fully-qualified test IDs for the Dialog component */
  dialog: {
    dismissButton: 'dialog-dismiss-button',
  },
} as const;
